import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PanelChevronComponent } from '../../../UI-Lib/Weissman-Panel/panel.chevron.component';
import {
    EntitySelection,
    GetFurtherLimitRequest,
    ReportDetail,
    ReportDetailCriteria,
    SelectedEntity,
} from 'src/app/Reporting/Manager/report.manager.model';
import { ReportType } from 'src/app/Reporting/Manager/report-type';
import { ReportManagerSelectEntitiesComponent } from '../../../Reporting/Manager/select.entities.component';
import { AttachmentBundlerService } from '../attachmentBundler.service';
import { ReportManagerSelectEntitiesService } from '../../../Reporting/Manager/select.entities.service';
import { ReportManagerService } from 'src/app/Reporting/Manager/report.manager.service';
import { FurtherLimitPanelAttachmentBundlerComponent } from 'src/app/Reporting/Manager/Further-Limit-Panel/further.limit.panel.attachment.bundler.component';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AttachmentBundlerContext } from '../../attachment.modal.model';
import { ToastrService } from 'ngx-toastr';
import { CompanyService } from 'src/app/Entity/Company/company.service';

import * as _ from 'lodash';

@Component({
    selector: 'bundler-filters',
    templateUrl: './bundlerFilters.component.html',
    styleUrls: ['./bundlerFilters.component.scss'],
    imports: [PanelChevronComponent, ReportManagerSelectEntitiesComponent, FurtherLimitPanelAttachmentBundlerComponent],
})
export class BundlerFiltersComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _attachmentBundlerService: AttachmentBundlerService,
        private readonly _reportManagerSelectEntitiesService: ReportManagerSelectEntitiesService,
        private readonly _reportManagerService: ReportManagerService,
        private readonly _toastrService: ToastrService,
        private readonly _companyService: CompanyService,
    ) {}

    @Input() filterScope: ReportType = ReportType.AttachmentBundlerCompany;

    loading: boolean = false;
    showFilters: boolean = true;
    showFurtherLimit: boolean = true;
    filtersReady: boolean = false;
    report: ReportDetail = new ReportDetail();
    availableYears: number[] = [];
    entityTypeFilters: number[] = [];

    private _dialogContext: AttachmentBundlerContext;
    private _destroy$: Subject<void> = new Subject();

    ngOnInit(): void {
        this._dialogContext = this._attachmentBundlerService.dialogContext;

        if (this._attachmentBundlerService.hasActiveFilter) {
            this.report = this._attachmentBundlerService.filterReport;
            this._finalizeInitialization();
        } else {
            this.report.criteria = new ReportDetailCriteria();
            this.report.criteria.reportType = this.filterScope;
            this.report.reportTypeId = this.filterScope;
            this.report.criteria.hideTopLevelCompanyPicker =
                this.filterScope === ReportType.AttachmentBundlerJurisdiction;
            this.report.criteria.entitySelection = this.report.criteria.entitySelection || new EntitySelection();
            this.report.criteria.entitySelection.beginWithStateSource =
                this.filterScope == ReportType.AttachmentBundlerJurisdiction;

            // Prepopulate from invoice context
            if (this._dialogContext) {
                void this._applyDialogContextDefaults().then(() => {
                    this._finalizeInitialization();
                    this._autoLoadFromInvoiceContext();
                });
            } else {
                this._finalizeInitialization();
            }
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    get hasSelection(): boolean {
        const selection = this.report.criteria.entitySelection;
        return (
            this.report.criteria.includeAllCompanies ||
            selection.selectedTopLevelCompanyIDs.length > 0 ||
            selection.selectedEntities.some((e) => e.selectedIDs.length > 0)
        );
    }

    onEntitySelectionChanged(): void {
        this.entityTypeFilters = _.map(this.report.criteria.entitySelection.selectedEntities, 'id');
    }

    async applyFilters(): Promise<void> {
        this.showFilters = false;
        this.showFurtherLimit = false;

        this._initializeEntityPayload();

        const furtherLimitRequest = new GetFurtherLimitRequest();
        furtherLimitRequest.startTaxYear = this.report.criteria.taxYearBegin;
        furtherLimitRequest.endTaxYear = this.report.criteria.taxYearEnd;
        furtherLimitRequest.includeBlankTaxYear = this.report.criteria.includeBlankTaxYear;
        furtherLimitRequest.attachmentLocations = this.report.criteria.attachmentLocations;
        furtherLimitRequest.attachmentTypes = this.report.criteria.attachmentTypes;
        furtherLimitRequest.propertyTypes = this._getPropertyTypesFilter();

        const searchCompleted = await this._attachmentBundlerService.getAttachmentsFromFurtherLimitFilter(
            furtherLimitRequest,
            this.report,
        );

        if (!searchCompleted) {
            this.showFilters = true;
            this.showFurtherLimit = true;
        }
    }

    getEntityTypeFilters(): number[] {
        return _.map(this.report.criteria.entitySelection.selectedEntities, 'id');
    }

    async onFurtherLimitSelectionChanged(): Promise<boolean> {
        const furtherLimitRequest = new GetFurtherLimitRequest();
        furtherLimitRequest.startTaxYear = this.report.criteria.taxYearBegin;
        furtherLimitRequest.endTaxYear = this.report.criteria.taxYearEnd;
        furtherLimitRequest.includeBlankTaxYear = this.report.criteria.includeBlankTaxYear;
        furtherLimitRequest.attachmentLocations = this.report.criteria.attachmentLocations;
        furtherLimitRequest.attachmentTypes = this.report.criteria.attachmentTypes;
        furtherLimitRequest.propertyTypes = this._getPropertyTypesFilter();

        return await this._attachmentBundlerService.getAttachmentsFromFurtherLimitFilter(
            furtherLimitRequest,
            this.report,
        );
    }

    cancelLoading(): void {
        if (this._attachmentBundlerService.cancelActiveLoad()) {
            this._toastrService.info('Attachment loading was canceled.');
        }
    }

    private _initializeEntityPayload(): void {
        const dialogParcelIds = this._sanitizeEntityIds(this._dialogContext?.parcelIds);
        const dialogSiteIds = this._sanitizeEntityIds(this._dialogContext?.siteIds);
        let preferredSourceEntityTypeID: number | undefined;

        if (dialogParcelIds.length > 0) {
            preferredSourceEntityTypeID = Core.EntityTypes.Parcel;
        } else if (dialogSiteIds.length > 0) {
            preferredSourceEntityTypeID = Core.EntityTypes.Site;
        }

        const entitiesRequest = this._reportManagerSelectEntitiesService.getPayloadSourceEntityData(
            this.report.criteria.entitySelection,
            undefined,
            undefined,
            preferredSourceEntityTypeID,
        );
        this._attachmentBundlerService.getAttachmentsFromEntityFilter(entitiesRequest, this.report);
        this.entityTypeFilters = this.getEntityTypeFilters();
    }

    private _finalizeInitialization(): void {
        this._initializePropertyTypeFilterForScope();

        //further limit content
        this.availableYears = this._reportManagerService.getAvailableYears();
        this.entityTypeFilters = [];

        this._attachmentBundlerService.loading$.pipe(takeUntil(this._destroy$)).subscribe((x) => (this.loading = x));

        // Entity selection is now fully populated; allow the entity picker to render/initialize.
        this.filtersReady = true;
    }

    private async _applyDialogContextDefaults(): Promise<void> {
        const ctx = this._dialogContext;
        const siteIds = this._sanitizeEntityIds(ctx.siteIds);
        const parcelIds = this._sanitizeEntityIds(ctx.parcelIds);

        // Set entity selection with company/site
        const entitySelection = this.report.criteria.entitySelection;
        entitySelection.selectedTopLevelCompanyIDs = [];
        entitySelection.selectedEntities = [];

        // Route company context: subsidiaries to selectedEntities (flag=false),
        // top-level to selectedTopLevelCompanyIDs (flag=true to expand subsidiaries).
        if (ctx.companyId > 0) {
            if (ctx.isTopLevelCompany === false) {
                let topLevelCompanyId = ctx.topLevelCompanyId;

                // Prefer the top-level company id provided by the caller (invoice/referral).
                if (!topLevelCompanyId) {
                    // Fallback: resolve the top-level company for the subsidiary from the server.
                    try {
                        const topCompany = await this._companyService.getTopCompanyForEntity(
                            Core.EntityTypes.Company,
                            ctx.companyId,
                        );
                        topLevelCompanyId = topCompany?.companyID;
                    } catch {
                        this._toastrService.warning(
                            'Unable to determine the top-level company for the selected subsidiary. The subsidiary filter may not be preselected.',
                        );
                        return undefined;
                    }
                }

                if (topLevelCompanyId && topLevelCompanyId > 0) {
                    entitySelection.selectedTopLevelCompanyIDs = [topLevelCompanyId];
                }

                // Subsidiary: treat as source entity (flag=false)
                const companyEntity: SelectedEntity = {
                    name: 'Subsidiary Companies',
                    buttonName: 'Subsidiaries',
                    id: Core.EntityTypes.Company,
                    prerequisites: [],
                    blockers: [],
                    list: [{ id: ctx.companyId, name: '' }],
                    selectedIDs: [ctx.companyId],
                    selectedPropCharVals: [],
                    dropdownList: [],
                    selectedDropdown: '',
                    loading: false,
                };
                entitySelection.selectedEntities.push(companyEntity);
            } else {
                // Top-level: expands to all subsidiaries via backend query
                entitySelection.selectedTopLevelCompanyIDs = [ctx.companyId];
            }
        }

        // Build selected entities for site + parcels
        if (siteIds.length > 0) {
            const siteEntity: SelectedEntity = {
                name: 'Site',
                buttonName: 'Site',
                id: Core.EntityTypes.Site,
                prerequisites: [],
                blockers: [],
                list: siteIds.map((x) => ({ id: x, name: '' })),
                selectedIDs: siteIds,
                selectedPropCharVals: [],
                dropdownList: [],
                selectedDropdown: '',
                loading: false,
            };
            entitySelection.selectedEntities.push(siteEntity);
        }

        if (parcelIds.length > 0) {
            const parcelEntity: SelectedEntity = {
                name: 'Parcel',
                buttonName: 'Parcel',
                id: Core.EntityTypes.Parcel,
                prerequisites: [],
                blockers: [],
                list: parcelIds.map((id) => ({ id, name: '' })),
                selectedIDs: parcelIds,
                selectedPropCharVals: [],
                dropdownList: [],
                selectedDropdown: '',
                loading: false,
            };
            entitySelection.selectedEntities.push(parcelEntity);
        }

        // Set further-limit defaults
        if (ctx.year) {
            this.report.criteria.taxYearBegin = ctx.year;
            this.report.criteria.taxYearEnd = ctx.year;
        }
        if (ctx.defaultLocations?.length) {
            this.report.criteria.attachmentLocations = ctx.defaultLocations;
        }
        if (ctx.defaultCategories?.length) {
            this.report.criteria.attachmentCategories = ctx.defaultCategories;
        }
        if (ctx.defaultAttachmentTypes?.length) {
            this.report.criteria.attachmentTypes = [...ctx.defaultAttachmentTypes];
        }
    }

    private async _autoLoadFromInvoiceContext(): Promise<void> {
        // Defensive guard: ensure dialog context is valid for entity initialization
        if (!this._isValidContextForAutoLoad()) {
            this._toastrService.warning('Unable to auto-load attachments. Insufficient context for entity selection.');
            return;
        }

        // applyFilters() already initializes the entity payload and performs the further-limit
        // search; it also manages showFilters/showFurtherLimit based on whether the search
        // succeeded. Calling it a second time here duplicated the getList request and caused
        // the bundler to appear unresponsive while the redundant response was processed.
        await this.applyFilters();
    }

    private _isValidContextForAutoLoad(): boolean {
        if (!this._dialogContext) {
            return false;
        }
        // Company context alone is sufficient; site/parcel selections are optional pre-filters.
        return this._dialogContext.companyId > 0;
    }

    private _sanitizeEntityIds(ids: number[] | null | undefined): number[] {
        return (ids || []).filter((id): id is number => Number.isInteger(id) && id > 0);
    }

    private _initializePropertyTypeFilterForScope(): void {
        this.report.criteria = this.report.criteria || new ReportDetailCriteria();
        if (this.filterScope === ReportType.AttachmentBundlerJurisdiction) {
            this.report.criteria.propertyTypeIDs = [];
        }
    }

    private _getPropertyTypesFilter(): Core.PropertyTypes[] {
        if (this.filterScope === ReportType.AttachmentBundlerJurisdiction) {
            return [];
        }

        return this.report.criteria.propertyTypeIDs || [];
    }
}
